import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';

/** Headcount grid — matches dashboard reference */
const MAIN_HEADER_BG = '#1e3a8a';
const MAIN_HEADER_FG = '#ffffff';
const BORDER_LIGHT = '#e2e8f0';
const CELL_BORDER = `1px solid ${BORDER_LIGHT}`;
const ROW_STRIPE_A = '#ffffff';
const ROW_STRIPE_B = '#f8fafc';
const COMPUTED_COL_BG = '#dbeafe';
const TEXT_PRIMARY = '#0f172a';

/** Top-line KPIs — label column gray, value column light blue */
const KPI_LABEL_BG = '#f1f5f9';
const KPI_VALUE_BG = '#dbeafe';

const KPI_LABEL_CELL = {
  backgroundColor: KPI_LABEL_BG,
  border: CELL_BORDER,
  color: TEXT_PRIMARY,
  padding: '12px 16px',
  verticalAlign: 'middle',
  textAlign: 'left',
};

const KPI_VALUE_CELL = {
  backgroundColor: KPI_VALUE_BG,
  border: CELL_BORDER,
  color: TEXT_PRIMARY,
  padding: '12px 16px',
  verticalAlign: 'middle',
  textAlign: 'center',
  fontWeight: 700,
  minWidth: 100,
  width: 120,
};

const riskLabelMap = [
  { key: 'totalActiveHeadcount', label: 'Total active headcount' },
  { key: 'totalHired', label: 'Total hired' },
  { key: 'openReq', label: 'Open req' },
  { key: 'futureGap', label: 'Future gap' },
  { key: 'atRisk', label: 'At risk' },
  { key: 'positiveOut', label: 'Positive out' },
  { key: 'yetToJoin', label: 'Yet to join' },
];

function HrRacksheetOverallSummary(props) {
  const [summaryPayload, setSummaryPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterCompanyWideSummary();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load company-wide summary',
          'error'
        );
        setSummaryPayload(null);
        return;
      }
      setSummaryPayload(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading roster company-wide summary:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading company-wide summary',
        'error'
      );
      setSummaryPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const table = summaryPayload?.table;
  const statusColumns = Array.isArray(table?.statusColumns) ? table.statusColumns : [];
  const computedColumns = Array.isArray(table?.computedColumns) ? table.computedColumns : [];
  const tableRows = Array.isArray(table?.rows) ? table.rows : [];
  const grandTotal = table?.grandTotal;
  const topRisk = summaryPayload?.topRiskMatrix;
  const topLineKpis = summaryPayload?.topLineKpis;
  const rawDeptCol = String(table?.departmentColumnLabel || '').trim();
  const deptLabel =
    !rawDeptCol ||
    rawDeptCol.toLowerCase() === 'department' ||
    rawDeptCol.toLowerCase() === 'function'
      ? 'Department'
      : rawDeptCol;

  const renderTopLineKpisTable = () => {
    const rows = Array.isArray(topLineKpis?.rows) ? topLineKpis.rows : [];
    if (rows.length === 0) return null;

    return (
      <Box mt={4} mb={1} maxWidth={960}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 700, marginBottom: 10, color: TEXT_PRIMARY }}
        >
          {topLineKpis.title || 'Top-line KPIs'}
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          style={{ border: CELL_BORDER, borderRadius: 0 }}
        >
          <Table size="small" style={{ borderCollapse: 'collapse' }}>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key || row.label}>
                  <TableCell style={KPI_LABEL_CELL}>{row.label}</TableCell>
                  <TableCell align="center" style={KPI_VALUE_CELL}>
                    {row.value ?? '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderLegacyRiskMatrix = () => {
    if (topLineKpis?.rows?.length) return null;
    if (!topRisk || typeof topRisk !== 'object') return null;
    return (
      <Box mb={3}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
          Top risk matrix
        </Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
          {riskLabelMap.map(({ key, label }) => (
            <Paper
              key={key}
              elevation={1}
              style={{ padding: '12px 16px', minWidth: 140, backgroundColor: '#fafafa' }}
            >
              <Typography variant="caption" color="textSecondary">
                {label}
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {topRisk[key] ?? '-'}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  const renderTable = () => {
    if (!table || statusColumns.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No summary table data available.
        </Typography>
      );
    }

    const allBodyRows = [...tableRows];
    if (grandTotal && grandTotal.rowType === 'grandTotal') {
      allBodyRows.push(grandTotal);
    }

    const headerCellBase = {
      backgroundColor: MAIN_HEADER_BG,
      color: MAIN_HEADER_FG,
      fontWeight: 700,
      border: CELL_BORDER,
      padding: '12px 10px',
    };

    return (
      <Box mb={1}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 700, marginBottom: 10, color: TEXT_PRIMARY }}
        >
          Headcount by Department × Status
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          style={{
            maxWidth: '100%',
            overflowX: 'auto',
            border: CELL_BORDER,
            borderRadius: 0,
          }}
        >
          <Table size="small" stickyHeader style={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    ...headerCellBase,
                    textAlign: 'left',
                    minWidth: 168,
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    boxShadow: '2px 0 4px rgba(15,23,42,0.06)',
                  }}
                >
                  {deptLabel}
                </TableCell>
                {statusColumns.map((col) => (
                  <TableCell
                    key={col.field || col.label}
                    align="center"
                    style={{
                      ...headerCellBase,
                      minWidth: 84,
                      zIndex: 3,
                    }}
                    title={col.meaning || col.label}
                  >
                    {col.label}
                  </TableCell>
                ))}
                {computedColumns.map((col) => (
                  <TableCell
                    key={col.field}
                    align="center"
                    style={{
                      ...headerCellBase,
                      minWidth: 104,
                      zIndex: 3,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allBodyRows.map((row, idx) => {
                const isGrand =
                  row.rowType === 'grandTotal' ||
                  String(row.departmentName || '').toLowerCase() === 'grand total';
                const isOthers = row.rowType === 'others';
                const values = Array.isArray(row.values) ? row.values : [];
                const stripeBg = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
                const rowBg = isGrand ? MAIN_HEADER_BG : stripeBg;
                const rowFg = isGrand ? MAIN_HEADER_FG : TEXT_PRIMARY;

                return (
                  <TableRow key={`${row.departmentId || 'row'}-${idx}`}>
                    <TableCell
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: rowBg,
                        color: rowFg,
                        fontWeight: 700,
                        fontStyle: isOthers ? 'italic' : undefined,
                        border: CELL_BORDER,
                        padding: '10px 12px',
                        textAlign: 'left',
                        boxShadow: isGrand ? undefined : '2px 0 4px rgba(15,23,42,0.04)',
                      }}
                    >
                      {row.departmentName || '-'}
                    </TableCell>
                    {statusColumns.map((col, colIdx) => {
                      const v = values[colIdx];
                      return (
                        <TableCell
                          key={col.field}
                          align="center"
                          style={{
                            backgroundColor: rowBg,
                            color: rowFg,
                            fontWeight: isGrand ? 700 : 400,
                            border: CELL_BORDER,
                            padding: '10px 8px',
                          }}
                        >
                          {v ?? '-'}
                        </TableCell>
                      );
                    })}
                    {computedColumns.map((col) => (
                      <TableCell
                        key={col.field}
                        align="center"
                        style={{
                          backgroundColor: isGrand ? MAIN_HEADER_BG : COMPUTED_COL_BG,
                          color: rowFg,
                          fontWeight: isGrand ? 700 : 600,
                          border: CELL_BORDER,
                          padding: '10px 8px',
                        }}
                      >
                        {row[col.field] ?? '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Typography variant="h5" style={{ fontWeight: 700, color: TEXT_PRIMARY }}>
          Headcount Summary — Company-wide
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          disabled={loading}
          onClick={() => loadSummary()}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>
      {renderTable()}
      {renderTopLineKpisTable()}
      {renderLegacyRiskMatrix()}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetOverallSummary);
